// apps/frontend-vario-kaufland/src/lib/category-tree.ts
//
// Reine, DOM-freie Logik fuer den aufklappbaren Kategoriebaum im Modal
// "Kategorien waehlen fuer Kaufland …" (settings.ts). Hier steht KEIN Markup und kein
// Zugriff auf document -- das Rendern bleibt in settings.ts, damit diese Regeln testbar
// sind, ohne ein DOM aufzubauen.
//
// Warum ueberhaupt ein Baum: Der DE-Katalog hat 6.087 Kategorien. Wer seine Kategorie
// nicht namentlich kennt, findet sie ueber die Suche nicht. Der Baum ist der zweite Weg,
// und er sitzt an derselben Stelle wie die Trefferliste (leeres Suchfeld -> Baum).
//
// Warum nur die aufgeklappten Aeste: Ein Voll-Render aller 6.087 Zeilen ist im iframe
// spuerbar traege. sichtbareBaumZeilen() liefert deshalb nur, was der Haendler gerade
// aufgeklappt hat.

import type { KategorieKatalogEintrag } from './category-path.js';

/** Kinder-Index und Wurzelliste, einmal aus dem Katalog gebaut. */
export interface KategorieBaumIndex {
  /** category_id des Elternteils -> dessen direkte Kinder, nach Anzeigename sortiert. */
  kinder: Map<number, number[]>;
  /** Alle Wurzelkategorien, nach Anzeigename sortiert. */
  wurzeln: number[];
}

/** Eine sichtbare Zeile des Baums, flach -- die Einrueckung steckt in `ebene`. */
export interface KategorieBaumZeile {
  categoryId: number;
  name: string;
  /** 0 fuer eine Wurzel, +1 je Stufe darunter. */
  ebene: number;
  /** Kein Kind im Katalog. Nur Blaetter sind ankreuzbar, wie in der Trefferliste. */
  istBlatt: boolean;
  /** Anzahl der DIREKTEN Kinder. Bei einem Blatt 0. */
  kinderAnzahl: number;
  /** Ob dieser Knoten gerade aufgeklappt ist. Bei einem Blatt immer false. */
  offen: boolean;
}

/** Sortierung der Geschwister: Anzeigename, deutsche Kollation (Umlaute einsortiert statt
 *  hinter Z). Gleichnamige Geschwister behalten eine stabile Reihenfolge ueber die ID. */
function vergleicheNachName(
  katalog: Map<number, KategorieKatalogEintrag>,
  a: number,
  b: number,
): number {
  const na = katalog.get(a)?.name ?? String(a);
  const nb = katalog.get(b)?.name ?? String(b);
  const cmp = na.localeCompare(nb, 'de');
  return cmp !== 0 ? cmp : a - b;
}

/**
 * Baut aus dem flachen Katalog den Kinder-Index und die Wurzelliste.
 *
 * Wurzelerkennung, drei Faelle. Nur der zweite kommt in den echten Daten vor, die beiden
 * anderen sind Absicherung gegen unvollstaendige Katalogdaten:
 *  1. `parentId === null` (Spalte leer). In keiner der sieben befuellten Storefronts
 *     vorhanden, live geprueft -- die Regel steht hier, weil der Typ `number | null` sie
 *     zulaesst und ein Katalog ohne sie stillschweigend wurzellos waere.
 *  2. `parentId === 0` -- DAS ist der Live-Fall, genau einmal je Storefront
 *     (`category_id = 1` fuehrt `parent_id = 0`). 0 ist keine gueltige Kategorie-ID,
 *     sondern Kauflands Schreibweise fuer "keine".
 *  3. der Elternteil steht nicht im Katalog. Live gibt es keine solche Waise. Ohne den Fall
 *     verschwaende aber ein ganzer Ast lautlos, sobald eine Zwischenzeile fehlt -- und genau
 *     das ist bei einem geblaetterten Abruf ueber 6.087 Zeilen kein theoretisches Risiko.
 */
export function baueKategorieBaum(
  katalog: Map<number, KategorieKatalogEintrag>,
): KategorieBaumIndex {
  const kinder = new Map<number, number[]>();
  const wurzeln: number[] = [];

  for (const [id, eintrag] of katalog) {
    const eltern = eintrag.parentId;
    const istWurzel = eltern === null || eltern === 0 || !katalog.has(eltern);
    if (istWurzel) {
      wurzeln.push(id);
      continue;
    }
    const liste = kinder.get(eltern);
    if (liste) liste.push(id);
    else kinder.set(eltern, [id]);
  }

  wurzeln.sort((a, b) => vergleicheNachName(katalog, a, b));
  for (const liste of kinder.values()) {
    liste.sort((a, b) => vergleicheNachName(katalog, a, b));
  }
  return { kinder, wurzeln };
}

/**
 * Liefert die Knoten, die beim ERSTEN Aufbau des Baums schon aufgeklappt sein sollen.
 *
 * Warum das noetig ist: Kauflands Katalog haengt alles unter EINEN Sammelknoten. Fuer `de`
 * fuehrt `kaufland.categories` genau eine Wurzel (`category_id = 1`, `parent_id = 0`) mit 13
 * direkten Kindern, den echten Hauptkategorien (Auto & Motorrad, Baby & Kind, Elektronik &
 * Computer und so weiter). Ohne Vorbelegung saehe der Haendler beim Oeffnen eine einzige
 * Zeile und muesste erst klicken, bevor ueberhaupt etwas Brauchbares erscheint; der Baum
 * wirkte leer, und eine Ebene Einrueckung waere dauerhaft verschenkt.
 *
 * Die Regel haengt an der ANZAHL der Wurzeln, nicht am Namen. `title_singular` dieser Wurzel
 * ist je Storefront uebersetzt (`de`/`at` "Alle Kategorien", `fr` "Toutes les categories",
 * `pl` "Wszystkie kategorie"); eine Pruefung auf `name === 'root'` braeche, sobald Kaufland
 * die Uebersetzung aendert. Bei mehreren Wurzeln bleibt alles zugeklappt.
 *
 * Es ist eine VORBELEGUNG, keine Sperre. Der Aufrufer nimmt das Ergebnis EINMAL beim Aufbau
 * entgegen und pflegt seinen Aufklappzustand danach selbst -- deshalb steht das hier und
 * nicht in sichtbareBaumZeilen: von dort aus wuerde jedes Rendern ein Zuklappen der Wurzel
 * sofort wieder rueckgaengig machen.
 */
export function anfangsOffeneKnoten(index: KategorieBaumIndex): Set<number> {
  const offen = new Set<number>();
  if (index.wurzeln.length !== 1) return offen;
  const [wurzel] = index.wurzeln;
  if (wurzel === undefined) return offen;
  if ((index.kinder.get(wurzel) ?? []).length === 0) return offen;
  offen.add(wurzel);
  return offen;
}

/**
 * Erzeugt die flache Liste der aktuell SICHTBAREN Zeilen: alle Wurzeln, und darunter die
 * Kinder jedes Knotens, der in `offen` steht. Ein zugeklappter Knoten liefert seine Kinder
 * nicht -- daran haengt, dass der Baum bei 6.087 Kategorien schnell bleibt.
 *
 * `gesehen` schuetzt gegen einen Ring in fehlerhaften Katalogdaten, genau wie in
 * `kategorienPfad`. Ohne den Schutz liefe die Rekursion bei einem Ring endlos.
 */
export function sichtbareBaumZeilen(
  katalog: Map<number, KategorieKatalogEintrag>,
  index: KategorieBaumIndex,
  offen: ReadonlySet<number>,
): KategorieBaumZeile[] {
  const zeilen: KategorieBaumZeile[] = [];
  const gesehen = new Set<number>();

  const gehe = (id: number, ebene: number): void => {
    if (gesehen.has(id)) return;
    gesehen.add(id);
    const kinder = index.kinder.get(id) ?? [];
    const istBlatt = kinder.length === 0;
    const istOffen = !istBlatt && offen.has(id);
    zeilen.push({
      categoryId: id,
      name: katalog.get(id)?.name ?? String(id),
      ebene,
      istBlatt,
      kinderAnzahl: kinder.length,
      offen: istOffen,
    });
    if (!istOffen) return;
    for (const kind of kinder) gehe(kind, ebene + 1);
  };

  for (const wurzel of index.wurzeln) gehe(wurzel, 0);
  return zeilen;
}
